import React, { useEffect } from 'react';
import ChipLabelGuideline from '../components/common/ChipLabelGuideline';
import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import CustomTitleWithBlueBackground from '../components/common/CustomTitleWithBlueBackground';

import PFASAdviceGuidance from './PFASAdviceGuidance';
import TooltipFromDB from '../components/common/TooltipFromDB';
import { YourNasemScaleWithGuidelinesProps } from '../types/reports';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';
import ResultCTAOptions from './ResultCTAOptions';

type NasemLevelColors = {
  [key: string]: string;
};

export default function YourNasemScaleWithGuidelines({
  nasemLevel,
  displayNasemLevel,
  onToggle,
  isExpanded,
  addPadding = true,
  testId,
}: YourNasemScaleWithGuidelinesProps & {
  onToggle: () => void;
  isExpanded: boolean;
  addPadding?: boolean;
  testId: number;
}) {
  const { theme } = useThemeContext();
  const [selectedNasemLevel, setSelectedNasemLevel] = React.useState<string>(nasemLevel);
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setSelectedNasemLevel(nasemLevel);
  }, [nasemLevel]);

  const NASEM_LEVEL_COLORS: NasemLevelColors = {
    low: theme.custom.report.yourNasemScaleWithGuidelines.nasemLevelColors.low,
    medium: theme.custom.report.yourNasemScaleWithGuidelines.nasemLevelColors.medium,
    high: theme.custom.report.yourNasemScaleWithGuidelines.nasemLevelColors.high,
  };

  const renderNasemGuidelines = (
    yourNasemLevel: string,
    selectedNasemLevel: string,
    setSelectedNasemLevel: (namemLevel: string) => void,
    displayNasemLevel: boolean,
  ) => {
    const guidelines = [
      {
        label: '<2 ng/mL',
        backgroundColor:
          selectedNasemLevel === 'low'
            ? theme.custom.report.yourNasemScaleWithGuidelines.renderNasemGuidelines.guidelines.selected.low
            : theme.custom.report.yourNasemScaleWithGuidelines.renderNasemGuidelines.guidelines.default.low
                .backgroundColor,
        labelColor: NASEM_LEVEL_COLORS.low,
        borderColor: selectedNasemLevel === 'low' ? NASEM_LEVEL_COLORS.low : 'transparent',
        title: 'Low',
      },
      {
        label: '2-20 ng/mL',
        backgroundColor:
          selectedNasemLevel === 'medium'
            ? theme.custom.report.yourNasemScaleWithGuidelines.renderNasemGuidelines.guidelines.selected.medium
            : theme.custom.report.yourNasemScaleWithGuidelines.renderNasemGuidelines.guidelines.default.medium
                .backgroundColor,
        labelColor: NASEM_LEVEL_COLORS.medium,
        borderColor: selectedNasemLevel === 'medium' ? NASEM_LEVEL_COLORS.medium : 'transparent',
        title: 'Medium',
      },
      {
        label: '>20 ng/mL',
        backgroundColor:
          selectedNasemLevel === 'high'
            ? theme.custom.report.yourNasemScaleWithGuidelines.renderNasemGuidelines.guidelines.selected.high
            : theme.custom.report.yourNasemScaleWithGuidelines.renderNasemGuidelines.guidelines.default.high
                .backgroundColor,
        labelColor: NASEM_LEVEL_COLORS.high,
        borderColor: selectedNasemLevel === 'high' ? NASEM_LEVEL_COLORS.high : 'transparent',
        title: 'High',
      },
    ];

    return (
      <Box>
        {displayNasemLevel && (
          <Box sx={{ display: 'flex', gap: '5px', alignItems: 'flex-start' }}>
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                color: theme.custom.report.yourNasemScaleWithGuidelines.renderNasemGuidelines.yourCurrentPFASValueColor,
              }}
            >
              Your current PFAS values are {yourNasemLevel} level on the NASEM scale{' '}
            </Typography>
            <TooltipFromDB
              helpId="nasem-level"
              imageSxProps={{ mt: '2px' }}
              additionalCircularSxProps={{ width: '20px !important', height: '20px !important' }}
            />
          </Box>
        )}
        <Stack
          direction="row"
          spacing={{ xs: 1, md: 2 }}
          sx={{
            mb: 1,
            justifyContent: 'space-between',
            alignItems: 'stretch',
          }}
        >
          {guidelines.map((guideline, index) => (
            <Box
              key={index}
              sx={{
                flex: 1,
                maxWidth: 'calc(33.33% - 8px)',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  cursor: 'pointer',
                },
              }}
              onClick={() => setSelectedNasemLevel(guideline.title.toLowerCase())}
            >
              <ChipLabelGuideline
                label={guideline.label}
                sxProps={{
                  backgroundColor: guideline.backgroundColor,
                  padding: { xs: '0px', md: '4px 25px' },
                  '& .MuiChip-label': {
                    '@media (max-width: 384px)': {
                      fontSize: '13px',
                    },
                    padding: '0px',
                  },
                }}
                labelColor={guideline.labelColor}
                borderColor={guideline.borderColor}
              />
              <Box sx={{ marginTop: '5px' }}>
                <Typography variant="body2" color={guideline.labelColor}>
                  {guideline.title}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  const RenderYourNameScale = (
    selectedNasemLevel: string,
    displayNasemLevel: boolean,
    onToggle: () => void,
    isExpanded: boolean,
  ) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const nasemLevelColor: string = NASEM_LEVEL_COLORS[selectedNasemLevel];
    const nasemLevelBackgroundColor: string =
      selectedNasemLevel === 'low'
        ? theme.custom.report.yourNasemScaleWithGuidelines.renderNasemGuidelines.guidelines.selected.low.backgroundColor
        : selectedNasemLevel === 'medium'
          ? theme.custom.report.yourNasemScaleWithGuidelines.renderNasemGuidelines.guidelines.selected.medium
              .backgroundColor
          : selectedNasemLevel === 'high'
            ? theme.custom.report.yourNasemScaleWithGuidelines.renderNasemGuidelines.guidelines.selected.high
                .backgroundColor
            : theme.custom.report.yourNasemScaleWithGuidelines.renderNasemGuidelines.guidelines.default.color;

    const nasemTextTitle: { [key: string]: string } = {
      low: isMobile
        ? 'No special requirements. Your physician should\nprovide you with the usual standard of care.'
        : 'No special requirements. Your physician should provide you with the usual standard of care.',
      medium: 'Reduce PFAS exposure, especially if pregnant.',
      high: isMobile
        ? 'Identify sources of PFAS exposure and reduce if\npossible. This is especially important if you are\npregnant.'
        : 'Identify sources of PFAS exposure and reduce if possible. This is especially important if you are pregnant.',
    };

    const nasemTextSubTitle: { [key: string]: any } = {
      low: <></>,
      medium: 'In addition to the usual standard of care, your physician should:',
      high: 'In addition to the usual standard of care, your clinician should:',
    };

    // The text for the high level is too long to fit in a single line for desktop
    // hence making it full width for all viewports
    const mdFlexDirection = selectedNasemLevel === 'high' ? 'column' : 'row';

    const renderCollapseBtn = () => {
      return (
        <Box
          onClick={onToggle}
          sx={{
            display: { xs: 'flex', md: 'none' },
            width: '40px',
            height: '40px',
            padding: '10px',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            borderRadius: '16px',
            background:
              theme.custom.report.yourNasemScaleWithGuidelines.renderYourNasemScale.renderCollapseBtn.backgroundColor,
            cursor: 'pointer',
          }}
        >
          <Box
            component="img"
            src={theme.custom.report.yourNasemScaleWithGuidelines.renderYourNasemScale.renderCollapseBtn.collapseIcon}
            sx={{
              display: 'flex',
              width: '24px',
              height: '24px',
              justifyContent: 'center',
              alignItems: 'center',
              flexShrink: 0,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
            }}
          />
        </Box>
      );
    };

    return (
      <Box mb="20px">
        <Box
          sx={{
            color: theme.custom.report.yourNasemScaleWithGuidelines.renderYourNasemScale.nasemGuidance.color,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            gap: '16px',
            flexDirection: { xs: 'row', md: 'row' },
            mb: 2,
            mt: 2,
            textAlign: 'left',
          }}
        >
          <Typography variant="h6" sx={{ flex: 1 }}>
            NASEM guidance for
            <span
              style={{ color: nasemLevelColor, backgroundColor: nasemLevelBackgroundColor, display: 'inline-block' }}
            >
              &nbsp;{selectedNasemLevel} level&nbsp;
            </span>
            score
          </Typography>
          {renderCollapseBtn()}
        </Box>
        {(isExpanded || !isMobile) && (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', md: mdFlexDirection },
              }}
            >
              <Box mb={selectedNasemLevel === 'high' ? '11px' : '0px'} sx={{ textAlign: 'left' }}>
                <CustomTitleWithBlueBackground
                  text={nasemTextTitle[selectedNasemLevel]}
                  gutterBottom={false}
                  variant="body2"
                  sx={{ fontSize: '15px', padding: '0px 5px' }}
                />
              </Box>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: '15px',
                fontWeight: 'bold',
                mt: '10px',
                mb: 2,
                color: theme.custom.report.yourNasemScaleWithGuidelines.renderYourNasemScale.nasemTextSubTitle.color,
                textAlign: 'left',
              }}
            >
              {nasemTextSubTitle[selectedNasemLevel]}
            </Typography>
            <PFASAdviceGuidance nasemLevel={selectedNasemLevel} displayNasemLevel={displayNasemLevel} />
          </>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.custom.report.yourNasemScaleWithGuidelines.backgroundColor,
        padding: addPadding ? { sm: '40px', xs: '24px 16px' } : 0,
        borderRadius: '16px',
      }}
    >
      {renderNasemGuidelines(nasemLevel, selectedNasemLevel, setSelectedNasemLevel, displayNasemLevel)}
      {RenderYourNameScale(selectedNasemLevel, displayNasemLevel, onToggle, isExpanded)}
      {(!isSmallDevice || isExpanded) && <ResultCTAOptions showUnderstandTestButton={true} testId={testId} />}
    </Box>
  );
}
